import axios from 'axios';

// 1. Parse keys from environment
const rawKeys = process.env.REACT_APP_MORALIS_API_KEYS || process.env.REACT_APP_MORALIS_API_KEY || '';
const keys = rawKeys
  .split(',')
  .map(s => s.trim())
  .filter(Boolean);

// If no keys found, we might warn, but let's proceed (it will fail on request if empty)
if (!keys.length) {
  console.warn('No REACT_APP_MORALIS_API_KEYS found in environment.');
}

// 2. Configuration
const MAX_RETRIES = keys.length > 0 ? keys.length - 1 : 0;
const COOLDOWN_MS = 300000; // 5 minutes per key if rate-limited
const RETRY_DELAY_MS = 150;

// 3. State
let ptr = 0;
const cooldownUntil = new Array(keys.length).fill(0);

// Helper: current time
const now = () => Date.now();

// Helper: pick next usable key
function pickKey() {
  if (!keys.length) return { key: '', idx: -1 };

  // Try to find a non-cooled key starting from ptr
  for (let i = 0; i < keys.length; i++) {
    const idx = (ptr + i) % keys.length;
    if (cooldownUntil[idx] <= now()) {
      ptr = idx;
      return { key: keys[idx], idx };
    }
  }

  // All keys cooled? Pick the one that expires soonest
  let minIdx = 0;
  for (let i = 1; i < keys.length; i++) {
    if (cooldownUntil[i] < cooldownUntil[minIdx]) {
      minIdx = i;
    }
  }
  // (Optional) Log warning: "All keys cooled, forcing key#..."
  return { key: keys[minIdx], idx: minIdx };
}

// Helper: mark key as cooled
function markCooldown(idx) {
  if (idx >= 0 && idx < keys.length) {
    cooldownUntil[idx] = now() + COOLDOWN_MS;
    // Advance pointer to try next key immediately next time
    ptr = (idx + 1) % keys.length;
  }
}

// 4. Create Axios Instance
const moralisClient = axios.create();

// Request Interceptor: Inject Key
moralisClient.interceptors.request.use((config) => {
  const { key, idx } = pickKey();
  if (key) {
    config.headers['X-API-Key'] = key;
    config.__moralisIdx = idx;
  }
  return config;
});

// Response Interceptor: Rotate on 429/401/403
moralisClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const config = error.config;
    if (!config || !keys.length) return Promise.reject(error);

    const status = error.response?.status;
    const body = error.response?.data;
    
    // Check if it's a rate-limit or auth error we want to rotate on
    // 401/403 often means "Quota Limit Reached" for Moralis free tier
    const isRateLimit = 
      status === 429 || 
      status === 402 || 
      status === 401 || 
      status === 403 ||
      (typeof body === 'string' && /rate|limit|quota/i.test(body));

    const isServerErr = status >= 500 && status <= 599;

    const retries = config.__moralisRetries || 0;

    if ((isRateLimit || isServerErr) && retries < MAX_RETRIES) {
      // Mark current key as cooled if it was a rate limit
      const idx = config.__moralisIdx;
      if (isRateLimit && typeof idx === 'number') {
        markCooldown(idx);
      }

      // Increment retry count
      config.__moralisRetries = retries + 1;

      // Pick new key (interceptor logic above will be bypassed if we don't manually set header, 
      // BUT actually we just want to re-run the request. 
      // The request interceptor runs again for new requests? 
      // No, axios.request(config) might skip interceptors if we are not careful, 
      // but usually it runs them. However, to be safe, let's manually update the header here.)
      
      const { key: nextKey, idx: nextIdx } = pickKey();
      config.headers['X-API-Key'] = nextKey;
      config.__moralisIdx = nextIdx;

      // Delay
      await new Promise(r => setTimeout(r, RETRY_DELAY_MS));

      // Retry
      return moralisClient.request(config);
    }

    return Promise.reject(error);
  }
);

export { moralisClient };
export default moralisClient;
